/**
 * Come si ordina un elenco di nomi che vengono da un fornitore.
 *
 * ## Il difetto
 *
 * `localeCompare` sui nomi veri ordina per **l'emoji iniziale**, perché è il primo carattere. Su una
 * installazione vera ogni categoria comincia così:
 *
 * ```
 * ⏩ | PLUS | CANALI +1
 * ⚽️ | DAZN | SERIE A
 * …
 * 📺 | SKY | INTRATTENIMENTO
 * 📽 | SKY | CINEMA SAT
 * ```
 *
 * L'ordine che ne esce è quello dei punti di codice Unicode delle emoji: per chi legge **non è
 * nessun ordine**, e somiglia a un ordine rovesciato — «Cinema» in fondo, «Dazn» in cima. Il nome
 * vero comincia dopo, e nessuno lo guardava.
 *
 * ## La chiave salta quel che non è una lettera
 *
 * Si scartano **solo i caratteri iniziali** che non sono lettere o cifre: emoji, stelline, barre
 * verticali, trattini e spazi. Quel che c'è dentro resta dov'è — `SKY | CINEMA` non diventa
 * `SKYCINEMA`, altrimenti due nomi diversi collasserebbero sullo stesso posto.
 *
 * ## `numeric` non è un ornamento
 *
 * Senza, «CANALI +10» viene prima di «CANALI +2», perché `1` precede `2` carattere per carattere. È
 * il genere di ordine che si nota solo quando si cerca una voce e non c'è dove dovrebbe stare.
 */

/**
 * Se un punto di codice è **scarto iniziale**: emoji, simbolo, punteggiatura, spazio.
 *
 * ## Perché a elenco di fasce e non `\p{L}`
 *
 * La versione di prima era `name.replace(/^[^\p{L}\p{N}]+/u, '')`, che è il modo giusto di scriverlo
 * e **non si può usare qui**: le proprietà Unicode nelle espressioni regolari sono di Chromium 64, e
 * Tizen 5 monta il **63**. Non è un ordinamento che sbaglia — è un `SyntaxError` quando il modulo
 * viene letto, cioè l'app che non parte affatto, schermo bianco. E non lo intercetta niente: non il
 * compilatore (`\p{…}` è ES2018, e il bersaglio è ES2018), non le prove (girano su Node moderno),
 * non la costruzione. Si vede solo sul televisore del cliente.
 *
 * ## Elenco di ciò che si scarta, non di ciò che si tiene
 *
 * Il verso conta. Dicendo «tengo le lettere» bisognerebbe saperle elencare tutte, e un alfabeto
 * dimenticato — cinese, arabo, ebraico — verrebbe **scartato per intero**, riducendo il nome a nulla.
 * Dicendo «scarto questi simboli», quel che non si conosce resta dov'è: il caso peggiore è un'emoji
 * nuova che non viene tolta, cioè il difetto di partenza su una voce sola.
 */
function isLeadingJunk(codePoint: number): boolean {
  // Spazio e punteggiatura ASCII, nelle quattro fasce fra le cifre e le lettere.
  if (codePoint <= 0x2f) return true;
  if (codePoint >= 0x3a && codePoint <= 0x40) return true;
  if (codePoint >= 0x5b && codePoint <= 0x60) return true;
  if (codePoint >= 0x7b && codePoint <= 0xbf) return true;
  // Punteggiatura generale, frecce, simboli matematici e vari: qui stanno ⏩ ⚽ ✨ ⭐ ✓ ►.
  if (codePoint >= 0x2000 && codePoint <= 0x2bff) return true;
  // I selettori di variante, che seguono un'emoji e da soli non si vedono: `⚽️` è ⚽ + U+FE0F.
  if (codePoint >= 0xfe00 && codePoint <= 0xfe0f) return true;
  // I piani supplementari, dove stanno le emoji vere: 📺 📽 🎬 🌟.
  return codePoint >= 0x1f000;
}

/**
 * La chiave con cui confrontare un nome di catalogo.
 *
 * Se il nome è **fatto solo** di simboli — capita: le righe separatrici di certi fornitori sono
 * `=== SPORT ===` o solo stelline — si torna al nome intero. Meglio un ordine strano che una chiave
 * vuota, che metterebbe tutte quelle voci insieme all'inizio senza un criterio.
 */
export function catalogSortKey(name: string): string {
  let at = 0;
  while (at < name.length) {
    // `codePointAt` e l'avanzamento di due: un'emoji è una coppia surrogata, e leggerla mezza
    // darebbe un punto di codice che non esiste — sempre dentro la fascia da scartare, quindi il
    // difetto non si vedrebbe fino al primo nome che comincia con una lettera fuori dal piano base.
    const codePoint = name.codePointAt(at);
    if (codePoint === undefined || !isLeadingJunk(codePoint)) break;
    at += codePoint > 0xffff ? 2 : 1;
  }
  const stripped = name.slice(at).trim();
  return stripped === '' ? name.trim() : stripped;
}

/**
 * Confronta due nomi di catalogo per come si leggono.
 *
 * A parità di chiave decide il nome intero: due categorie che differiscono **solo** per l'emoji
 * devono comunque avere un ordine stabile, o si scambiano di posto fra due disegni.
 */
export function compareCatalogNames(left: string, right: string): number {
  const byKey = catalogSortKey(left).localeCompare(catalogSortKey(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  return byKey !== 0 ? byKey : left.localeCompare(right);
}
